/**
 * Privacy Policy Page
 * Comprehensive privacy policy for CueSesh podcast app
 * Compliant with Apple App Store requirements
 */

import React from 'react';
import Header from '../components/Header';
import { MetaTags } from '../components/SEO';
import './Legal.css';

export default function Privacy() {
  return (
    <>
      <MetaTags
        title="Privacy Policy"
        description="CueSesh Privacy Policy. Learn how we collect, use, and protect your data. We don't sell your data or track you across apps."
        canonicalUrl="https://cuesesh.com/privacy"
        noindex={false}
      />

      <div className="legal-page">
        <Header />

        <div className="legal-page-with-header">
          <div className="legal-container">
            <h1 className="legal-title">Privacy Policy</h1>
            <p className="legal-subtitle">Last updated: July 14, 2026</p>

            <div className="legal-content">
              <p>
                At CueSesh, your privacy is fundamental to how we build our product. This policy explains
                what data we collect, why we collect it, and how we protect it. We've written this in
                plain language because you deserve to understand exactly what happens with your information.
              </p>

              <div className="legal-highlight">
                <strong>Our Privacy Promise:</strong> We do not sell your data. We do not share your data
                with third parties for advertising or tracking purposes. We do not track you across other
                apps or websites. Your data stays within CueSesh and is used solely to provide and improve
                your podcast listening experience.
              </div>

              <h2>Data We Collect</h2>
              <p>
                We collect only what's necessary to provide you with a great podcast listening experience.
                Here's exactly what we collect and why:
              </p>

              {/* This list mirrors PrivacyInfo.xcprivacy one-for-one (Email, Name,
                  UserID, ProductInteraction, SearchHistory, DeviceID — all Linked,
                  no Tracking) and the App Store Connect label. Nothing we collect
                  is unlinked; never reintroduce a "Not Linked" section without a
                  manifest change to match. */}
              <h3>Data Linked to Your Identity</h3>
              <ul>
                <li>
                  <strong>Email Address</strong> — Used for account authentication. Required to
                  create and secure your CueSesh account.
                </li>
                <li>
                  <strong>Name</strong> — Provided by Sign in with Apple or Google when you create
                  your account and stored as part of your account profile.
                </li>
                <li>
                  <strong>User ID (CueID)</strong> — A unique identifier for your account that enables
                  personalization features like saving your favorite podcasts, syncing playback progress
                  across devices, and maintaining your listening history.
                </li>
                <li>
                  <strong>Product Interaction</strong> — Your playback progress and queue, linked to
                  your account so your listening picks up where you left off on any device — and so
                  we can see how features are used and make them better. Never used for advertising,
                  never sold, and deleted entirely when you delete your account.
                </li>
                <li>
                  <strong>Search History</strong> — Your recent in-app searches, saved to your account
                  so you can pick them back up. Deleted when you delete your account.
                </li>
                <li>
                  <strong>Device Push Token</strong> — If you enable notifications, your device's push
                  token is stored with your account so we can deliver the notifications you opted into.
                </li>
              </ul>

              <h2>How We Use Your Data</h2>
              <p>Your data is used exclusively for the following purposes:</p>
              <ul>
                <li><strong>Account Management</strong> — Authenticate your identity and secure your account</li>
                <li><strong>Personalization</strong> — Save your preferences, listening history, and playback progress</li>
                <li><strong>Content Discovery</strong> — Recommend podcasts based on your interests and preferences</li>
                <li><strong>App Improvement</strong> — Understand how features are used so we can improve them and fix issues</li>
                <li><strong>Notifications</strong> — Send push notifications you've opted into, like new episodes from shows you follow</li>
              </ul>

              <h2>What We Don't Do</h2>
              <p>We believe it's just as important to tell you what we don't do with your data:</p>
              <ul>
                <li>We do <strong>NOT</strong> sell your personal data</li>
                <li>We do <strong>NOT</strong> use your data for cross-app tracking</li>
                <li>We do <strong>NOT</strong> share your data with data brokers</li>
                <li>We do <strong>NOT</strong> build advertising profiles based on your listening habits</li>
                <li>
                  We do <strong>NOT</strong> collect your location. If you use the commute feature,
                  your device asks Apple Maps for a travel time directly — the same way any maps app
                  gets directions. CueSesh never receives, accesses, or stores your location; only
                  the resulting travel time is used
                </li>
                <li>We do <strong>NOT</strong> access your contacts, photos, or other device data</li>
              </ul>

              <h2>Third-Party Services</h2>
              <p>We use the following third-party services to operate CueSesh:</p>
              <ul>
                <li>
                  <strong>Supabase</strong> — Provides secure authentication and database services. Your
                  account data is stored with industry-standard encryption. Supabase is SOC 2 Type II
                  compliant.
                </li>
                <li>
                  <strong>Google Analytics</strong> — Used on our website (cuesesh.com) for aggregate,
                  anonymized usage metrics; the iOS app contains no analytics SDK. This data is not
                  linked to your CueSesh account and cannot be used to identify you. We use it to make
                  informed decisions about the site and our marketing.
                </li>
              </ul>

              <h2>Where Your Data Is Stored</h2>
              <p>
                CueSesh is operated from the United States. Your data is stored and processed in the
                United States on infrastructure provided by Vercel, Neon, and Supabase (US regions),
                and may be subject to United States law. If you use CueSesh from outside the US —
                including Australia, New Zealand, the United Kingdom, or Canada — your information is
                transferred to and processed in the United States as described in this policy, and we
                take reasonable steps to ensure it is handled consistently with this policy wherever
                it is processed.
              </p>
              <p>
                For users in the European Union, the United Kingdom, and Switzerland, these
                transfers are covered by Standard Contractual Clauses incorporated into the
                data-processing agreements we hold with each of our processors (Vercel, Neon,
                and Supabase).
              </p>

              <h2>Advertising</h2>
              <p>
                We promote CueSesh through ads on platforms like Facebook, Instagram, and the
                App Store. To understand whether those ads actually bring people to CueSesh, we
                rely on <strong>Apple's SKAdNetwork</strong> — a privacy-preserving framework
                built into iOS. Apple sends us only aggregate, anonymous counts of installs that
                followed an ad. This does <strong>not</strong> identify you, does <strong>not</strong> track
                you across other apps, and shares <strong>none</strong> of your personal data.
              </p>
              <p>
                On our website (cuesesh.com), we also use the <strong>Meta Pixel</strong>. It helps
                us measure whether our Facebook and Instagram ads actually bring people to CueSesh,
                and lets us show our own ads to people who have visited the site. We use this for
                <strong> our own</strong> advertising — we do <strong>not</strong> sell your data to
                anyone. It runs <strong>only with your consent</strong>: you can decline it on the
                cookie banner, or signal an opt-out with your browser's Global Privacy Control, and
                it stays off.
              </p>

              <h2>Cookies &amp; Local Storage</h2>
              <p>
                On our website we use a small number of cookies and similar technologies:
                <strong>Google Analytics</strong> cookies (such as <em>_ga</em>) for aggregate,
                anonymized usage metrics, and — only after you accept on the cookie banner — the
                <strong> Meta Pixel</strong> cookie for the advertising measurement described above.
                You can accept or decline non-essential cookies on the banner shown on your first
                visit, change your choice anytime by clearing the site's storage, or opt out globally
                with your browser's Global Privacy Control. Declining does not affect your ability to
                use the site.
              </p>

              <h2>Podcast Content</h2>
              <p>
                CueSesh aggregates podcast content from publicly available RSS feeds and podcast APIs.
                We stream audio and video directly from original podcast hosting sources — we do not
                store podcast media files on our servers. The podcasts you listen to are delivered directly
                from the podcast creators' hosting providers.
              </p>
              <p>
                Podcast metadata (titles, descriptions, artwork) is cached temporarily to improve
                app performance and reduce load times.
              </p>

              <h2>Data Security</h2>
              <p>We implement industry-standard security measures to protect your data:</p>
              <ul>
                <li>All data transmission uses TLS/SSL encryption</li>
                <li>
                  Sign-in uses Sign in with Apple or Google — CueSesh never sees or stores a password
                </li>
                <li>Database access is restricted and monitored</li>
                <li>Regular security audits and updates</li>
              </ul>

              <h2>Data Retention</h2>
              <p>
                We retain your account data for as long as your account is active. If you delete your
                account, we will delete all associated data within 30 days, except where we are required
                by law to retain certain information.
              </p>
              <p>
                Aggregate website analytics (the Google Analytics metrics described above, from
                cuesesh.com) may be retained indefinitely; they are not linked to your CueSesh account.
              </p>

              <h2>Your Rights & Data Deletion</h2>
              <p>You have full control over your data. You can:</p>
              <ul>
                <li><strong>Access your data</strong> — Ask us for a copy of the personal data we hold about you</li>
                <li><strong>Correct your data</strong> — Update or fix any incorrect information</li>
                <li><strong>Delete your data</strong> — Self-service in the app (Settings &rarr; Delete account); removes your account and all associated data</li>
              </ul>
              <p>
                To exercise any of these rights, contact us at{' '}
                <a href="mailto:privacy@cuesesh.com">privacy@cuesesh.com</a>. We fulfill verified
                requests within 30 days.
              </p>

              <div className="legal-highlight">
                <strong>To delete your data:</strong> Open the CueSesh app and go to{' '}
                <strong>Settings &rarr; Delete account</strong> — it's self-service, no email
                required. This deletes all your data: account information, listening history,
                ratings, statistics, saved podcasts, and profile information. Deletion is permanent
                and cannot be undone. No longer have the app? Email{' '}
                <a href="mailto:privacy@cuesesh.com">privacy@cuesesh.com</a> from your account
                email and we'll handle it.
              </div>

              <p>
                If you believe we have not resolved your concern, you may lodge a complaint with your
                local data-protection authority — for example your member-state data-protection
                authority in the EU, the ICO (United Kingdom), the OAIC (Australia), the Office of
                the Privacy Commissioner of Canada, or the Office of the Privacy Commissioner
                (New Zealand).
              </p>

              <h2>GDPR Compliance (EU &amp; UK Users)</h2>
              <p>
                If you are located in the European Economic Area (EEA) or the United Kingdom, you
                have additional rights under the General Data Protection Regulation (GDPR) and
                UK GDPR:
              </p>
              <ul>
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
              </ul>
              <p>
                Our legal basis for processing your data is: (1) your consent when you create an account,
                (2) performance of a contract to provide our services, and (3) our legitimate interests
                in improving our product.
              </p>
              <p>
                <strong>EU, EEA &amp; UK Representative:</strong> CueSesh LLC has appointed{' '}
                <strong>DataRep</strong> as its data-protection representative under Article 27
                of the GDPR and the UK GDPR, so that individuals in the EU, EEA, and UK can
                contact a representative in their own region:
              </p>
              <p>
                Data Protection Representative Limited (trading as DataRep)<br />
                77 Camden Street Lower, Dublin, D02 XE80, Ireland<br />
                <a href="mailto:datarequest@datarep.com">datarequest@datarep.com</a> — please
                quote &ldquo;CueSesh LLC&rdquo; in the subject line<br />
                Online form:{' '}
                <a href="https://www.datarep.com/data-request" target="_blank" rel="noopener noreferrer">
                  www.datarep.com/data-request
                </a>
              </p>
              <p>
                DataRep maintains contact locations in each of the 27 EU member states, the UK,
                Norway, and Iceland. If writing by post, mark your letter for the attention of
                &ldquo;DataRep&rdquo; — not CueSesh LLC — or it may not reach them. For general
                questions about the CueSesh app or your account, please contact us directly at{' '}
                <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> rather than our
                representative.
              </p>

              <h2>CCPA Compliance (California Users)</h2>
              <p>
                If you are a California resident, the California Consumer Privacy Act (CCPA) provides
                you with specific rights:
              </p>
              <ul>
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether your personal information is sold or disclosed</li>
                <li>Right to say no to the sale of personal information (we don't sell data)</li>
                <li>Right to delete your personal information</li>
                <li>Right to non-discrimination for exercising your privacy rights</li>
              </ul>
              <p>
                <strong>We do not sell your personal information.</strong> We have not sold personal
                information in the preceding 12 months and have no plans to do so.
              </p>

              <h2>Children's Privacy</h2>
              <p>
                CueSesh is not intended for users under 13 years of age. We do not knowingly collect
                personal information from children under 13. If you are a parent or guardian and believe
                your child has provided us with personal information, please contact us immediately at{' '}
                <a href="mailto:privacy@cuesesh.com">privacy@cuesesh.com</a> and we will delete the
                information.
              </p>

              <h2>Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. When we make significant changes,
                we will notify you in the app. The "Last updated" date at the top of
                this page indicates when the policy was last revised.
              </p>
              <p>
                We encourage you to review this policy periodically to stay informed about how we
                protect your data.
              </p>

              <h2>Contact Us</h2>
              <p>
                CueSesh is provided by <strong>CueSesh LLC</strong>, the data controller for the
                personal data described in this policy. If you have any questions about this privacy
                policy, our data practices, or would like to exercise your privacy rights, please
                contact us:
              </p>
              <a href="mailto:privacy@cuesesh.com" className="contact-email-link">
                privacy@cuesesh.com
              </a>
              {/* Canonical public business address (virtual mailbox) — NEVER publish a
                  home address or the Somerset registered-filing address here. */}
              <p style={{ marginTop: '1rem' }}>
                CueSesh LLC<br />
                7216 Bergenline Ave #1054<br />
                North Bergen, NJ 07047, United States<br />
                Phone: (917) 830-5632
              </p>
              <p style={{ marginTop: '1.5rem', opacity: 0.7 }}>
                We respond to privacy-related inquiries as quickly as we can, and fulfill verified
                requests within 30 days.
              </p>
            </div>
          </div>
        </div>

        <footer className="legal-footer">
          <div className="legal-footer-links">
            <a href="/privacy" className="legal-footer-link">Privacy</a>
            <a href="/terms" className="legal-footer-link">Terms</a>
            <a href="/contact" className="legal-footer-link">Contact</a>
          </div>
          <p className="legal-footer-text">
            © 2026 CueSesh. All rights reserved.
          </p>
        </footer>
      </div>
    </>
  );
}
